import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  model,
  output,
  resource,
  signal,
  untracked,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ClarityModule, ClrLoadingState } from '@clr/angular';
import { ToastrService } from 'ngx-toastr';
import { DeselectSingleSelectedDirective } from 'src/app/directives/deselect-single-selected.directive';

import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { BaseDataRoute, BaseItemsHandler } from '../baseitems-wrapper.component';
import { TABLE_EMPTY } from 'src/app/models/ui/table/table';
import { BaseItem } from '../baseitems-wrapper.component';
import { errorHandle } from 'src/app/misc/functions';

@Component({
  selector: 'app-baseitems-swtyp-modal',
  templateUrl: './baseitems-swtyp-modal.component.html',
  styleUrl: './baseitems-swtyp-modal.component.scss',
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    DeselectSingleSelectedDirective,
    HideIDColumnsPipe,
    MrTranslatePipe,
    TablePrettyPrintPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class Baseitems_SWTYP_ModalComponent implements BaseItemsHandler {
  private readonly apiService = inject(APIService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly toastr = inject(ToastrService);

  readonly refresh = output<BaseItem[]>();
  readonly isModalOpen = model<boolean>();

  readonly route = model.required<BaseDataRoute>();
  readonly preSelected = computed(() => this.route().selected);

  protected readonly isRep = this.apiService.isRep;
  protected readonly bezeichnung = new FormControl('', Validators.required);
  protected readonly typ = new FormControl(SWTYP_TYP.Einfach, {
    nonNullable: true,
    validators: Validators.required,
  });
  protected readonly selectedRow = signal<SWTYP_Item | null>(null);

  private readonly tableResource = resource({
    params: () => this.isModalOpen() || undefined,
    loader: () => this.apiService.getBaseItems('SWTYP'),
    defaultValue: TABLE_EMPTY(),
  });
  protected readonly table = this.tableResource.value;
  protected readonly isLoading = this.tableResource.isLoading;
  protected readonly loadState = signal(ClrLoadingState.DEFAULT);
  private isChanged = false;

  constructor() {
    effect(() => this.bezeichnung.reset(this.selectedRow()?.Bezeichnung));

    effect(() => {
      const preSelect = this.preSelected();
      if (preSelect && this.table().columns.length)
        untracked(() => {
          this.route.update(route => ({ ...route, selected: null }));
          switch (typeof preSelect) {
            case 'string': {
              const found = this.table().rows.find(row => row.Bezeichnung == preSelect);
              if (found) this.selectedRow.set(found);
              else this.bezeichnung.setValue(preSelect);
              break;
            }
            case 'number': {
              this.selectedRow.set({ ID: preSelect } as SWTYP_Item);
              break;
            }
          }
        });
    });

    effect(() => this.typ.reset(this.selectedRow()?.Typ));
    effect(() => {
      const status = this.tableResource.status();
      untracked(() => {
        if (status == 'reloading') {
          this.isChanged = true;
        } else if (status == 'resolved' && this.isChanged) {
          this.isChanged = false;
          this.refresh.emit(this.table().rows);
        }
      });
    });
  }

  protected async onSubmit() {
    this.loadState.set(ClrLoadingState.LOADING);
    const sendObj: SWTYP_Item = {
      ID: this.selectedRow()?.ID,
      Bezeichnung: this.bezeichnung.value,
      Typ: this.typ.value,
    };

    try {
      const id = await this.apiService.setBaseItems('SWTYP', sendObj);

      this.loadState.set(ClrLoadingState.SUCCESS);
      this.toastr.success(this.mrTranslate.transform('Erfolgreich gespeichert'));
      this.tableResource.reload();
      this.selectedRow.set({ ID: id } as SWTYP_Item);
    } catch (er) {
      this.loadState.set(ClrLoadingState.DEFAULT);
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  protected async deleteRow() {
    try {
      await this.apiService.deleteBaseItems('SWTYP', this.selectedRow().ID);

      this.toastr.success(this.mrTranslate.transform('Eintrag wurde gelöscht'));
      this.tableResource.reload();
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }
}

const enum SWTYP_TYP {
  Undefined,
  Einfach,
  Doppelt,
  Kreuz,
}

export interface SWTYP_Item extends BaseItem {
  Typ: SWTYP_TYP;
}
